/**
 * Built-in effect `glitch` (W2-01b; P§5.2): horizontal block-shift glitch with an RGB split, driven by `time`.
 * Reimplemented from the behaviour (badged approximate).
 *
 * The screen is cut into `blockCount` horizontal bands. The pattern changes `speed` times per second (chart time,
 * so it is the same at every playback and seek). In each pattern a band glitches with probability `rate`: it shifts
 * sideways by up to `power` (fraction of the width) and its red and blue channels split apart by up to `colorRate`.
 */

import { m } from './builtinFx.messages';
import { defineBuiltin, HASH12, SAMPLE_SCREEN } from './shared';

export const glitch = defineBuiltin({
  name: 'glitch',
  label: { key: m.glitch },
  description: { key: m.glitchDesc },
  inputs: ['time'],
  uniforms: [
    { name: 'power', type: 'float', default: 0.03, min: 0, max: 0.3, label: { key: m.glitchPower } },
    { name: 'rate', type: 'float', default: 0.6, hint: '0..1', min: 0, max: 1, label: { key: m.glitchRate } },
    { name: 'speed', type: 'float', default: 5, min: 0, max: 60, label: { key: m.glitchSpeed } },
    { name: 'blockCount', type: 'float', default: 30.5, min: 1, max: 200, label: { key: m.glitchBlockCount } },
    {
      name: 'colorRate',
      type: 'float',
      default: 0.01,
      hint: '0..1',
      min: 0,
      max: 1,
      label: { key: m.glitchColorRate },
    },
  ],
  helpers: [SAMPLE_SCREEN, HASH12],
  main: `
void main() {
  float frame = floor(time * speed);
  float band = floor(uv.y * max(blockCount, 1.0));
  float shift = 0.0;
  float split = 0.0;
  if (hash12(vec2(band, frame)) < rate) {
    shift = (hash12(vec2(band + 31.7, frame + 3.1)) * 2.0 - 1.0) * power;
    split = (hash12(vec2(band + 7.9, frame + 57.3)) * 2.0 - 1.0) * colorRate;
  }
  vec2 p = vec2(uv.x + shift, uv.y);
  vec4 c = sampleScreen(p);
  float r = sampleScreen(p + vec2(split, 0.0)).r;
  float b = sampleScreen(p - vec2(split, 0.0)).b;
  gl_FragColor = vec4(r, c.g, b, c.a);
}`,
});
